import {
  ActionApprovalStatus,
  ActionStatus,
  ActionStatusNotActionable,
  SLAStatusEnum,
  UUIDV4_LENGTH,
  VARCHAR_MAX_LENGTH,
} from '@issuemanager/constants';
import slugify from 'slugify';
import { v4 as uuidv4 } from 'uuid';
import { ActionAttachmentQueryResponseType } from '../action-attachment';
import { GovmapGroupWithParentNameSchemaType } from '../govmap-group';
import {
  ActionCanNotBeUpdatedError,
  ActionCannotBeClosed,
  ActionCannotBeDeclinedError,
  ActionCannotBeStartedError,
  InvalidActionTransitionError,
  IssueAuthorizer,
  UserCanNotUpdateActionError,
} from '../issue';
import { ActorUserContext, MinimalUserEntity } from '../user';
import { ActionQueryResponseSchemaType } from './action.schema';

export type ActionUpdatableValues = Omit<
  ActionQueryResponseSchemaType,
  'status' | 'slug' | 'id'
>;

export type ActionConstructorParams = Pick<
  ActionQueryResponseSchemaType,
  'dueDate' | 'description' | 'approver' | 'owner' | 'title' | 'sequence'
> & {
  id?: string | null;
  govmapGroups?: ActionQueryResponseSchemaType['govmapGroups'] | null;
  status?: ActionQueryResponseSchemaType['status'];
  slaStatus?: ActionQueryResponseSchemaType['slaStatus'];
  addedByUser?: MinimalUserEntity | null;
  slugId?: ActionQueryResponseSchemaType['slugId'];
  declineNote?: ActionQueryResponseSchemaType['declineNote'];
  approvalStatus?: ActionQueryResponseSchemaType['approvalStatus'];
  attachments?: ActionQueryResponseSchemaType['attachments'];
  dueDate?: ActionQueryResponseSchemaType['dueDate'];
  updatedAt?: ActionQueryResponseSchemaType['updatedAt'];
  createdAt?: ActionQueryResponseSchemaType['createdAt'];
};

export type VisibleActionAttributes = Omit<
  ActionQueryResponseSchemaType,
  | 'slug'
  | 'id'
  | 'govmapGroups'
  | 'isActionOwner'
  | 'isActionApprover'
  | 'issueCode'
  | 'issueId'
  | 'approverId'
  | 'ownerId'
  | 'deletedAt'
> &
  Pick<ActionQueryResponseSchemaType, 'id' | 'slaStatus' | 'attachments'> & {
    owner: MinimalUserEntity | null;
    govmapGroups: GovmapGroupWithParentNameSchemaType[] | null;
    approvalStatus: ActionApprovalStatus;
    declineNote: string | null;
  };

export type OpenAction = {
  id: string;
  dueDate: Date;
  description: string;
  status: ActionStatus.OPEN;
  approver?: MinimalUserEntity | null;
  addedBy: MinimalUserEntity;
  owner: MinimalUserEntity;
  title: string;
  govmapGroups?: Readonly<GovmapGroupWithParentNameSchemaType[]> | null;
  slaStatus: SLAStatusEnum.NO_PROBLEM;
};

export type ActionUpdateValuesParams = Omit<
  ActionConstructorParams,
  'status' | 'id' | 'slug' | 'slaStatus'
>;

interface TransitionConfig {
  to: ActionStatus;
  action: string;
}

export class Action {
  private _id: string;

  private _dueDate?: ActionConstructorParams['dueDate'];

  private _description?: ActionConstructorParams['description'];

  private _status: ActionStatus;

  private _approver?: ActionConstructorParams['approver'];

  private _title: ActionConstructorParams['title'];

  private _govmapGroups?: ActionConstructorParams['govmapGroups'];

  private _owner?: ActionConstructorParams['owner'];

  private _slug?: ActionConstructorParams['slugId'];

  private _slaStatus?: ActionConstructorParams['slaStatus'];

  private _approvalStatus: ActionApprovalStatus = ActionApprovalStatus.NOT_SET;

  private _declineNote?: ActionConstructorParams['declineNote'];

  private _attachments: ActionConstructorParams['attachments'];

  private _sequence?: ActionConstructorParams['sequence'];

  private _updatedAt?: ActionConstructorParams['updatedAt'];

  private _createdAt?: ActionConstructorParams['createdAt'];

  private static readonly transitions: Record<
    ActionStatus,
    TransitionConfig[]
  > = {
    [ActionStatus.DRAFT]: [
      {
        to: ActionStatus.PENDING,
        action: 'Initiate',
      },
      {
        to: ActionStatus.OPEN,
        action: 'Open',
      },
    ],
    [ActionStatus.PENDING]: [
      {
        to: ActionStatus.OPEN,
        action: 'Initiate',
      },
      {
        to: ActionStatus.DISCARDED,
        action: 'Discard',
      },
    ],
    [ActionStatus.OPEN]: [
      {
        to: ActionStatus.IN_PROGRESS,
        action: 'Start',
      },
      {
        to: ActionStatus.DISCARDED,
        action: 'Discard',
      },
    ],
    [ActionStatus.IN_PROGRESS]: [
      {
        to: ActionStatus.TO_BE_APPROVED,
        action: 'MarkAsDone',
      },
      {
        to: ActionStatus.DISCARDED,
        action: 'Discard',
      },
      {
        to: ActionStatus.COMPLETED,
        action: 'Auto',
      },
    ],
    [ActionStatus.TO_BE_APPROVED]: [
      {
        to: ActionStatus.IN_PROGRESS,
        action: 'Decline',
      },
      {
        to: ActionStatus.COMPLETED,
        action: 'Approve',
      },
      {
        to: ActionStatus.DISCARDED,
        action: 'Discard',
      },
    ],
    [ActionStatus.COMPLETED]: [
      {
        to: ActionStatus.IN_PROGRESS,
        action: 'Decline',
      },
    ],
    [ActionStatus.DISCARDED]: [],
  };
  constructor(params: ActionConstructorParams) {
    this._dueDate = params.dueDate;
    this._description = params.description;
    this._status = params.status || ActionStatus.DRAFT;
    this._approver = params.approver;
    this._title = params.title;
    this._owner = params.owner;
    this._slaStatus = params.slaStatus;
    this._govmapGroups = params.govmapGroups
      ? [...params.govmapGroups]
      : params.govmapGroups;
    this._declineNote = params.declineNote;
    this._attachments = params.attachments || [];
    this._sequence = params.sequence;
    this._updatedAt = params.updatedAt;
    this._createdAt = params.createdAt;
    if (!params.id) {
      this._id = uuidv4();
    } else {
      this._id = params.id;
    }
    if (!params.slugId) {
      this._slug = this.generateSlug();
    } else {
      this._slug = params.slugId;
    }
  }

  get title() {
    return this._title;
  }

  get id() {
    return this._id;
  }

  get status() {
    return this._status;
  }

  get dueDate() {
    return this._dueDate;
  }

  get sequence() {
    return this._sequence;
  }

  get updatedAt() {
    return this._updatedAt;
  }

  get createdAt() {
    return this._createdAt;
  }

  /*
   @deprecated: approver should be check in the action status change methods RCP-16382
  */
  get approver() {
    return this._approver;
  }

  /*
   @deprecated: approvalStatus should be check in the action status change methods RCP-16382
  */
  get approvalStatus() {
    return this._approvalStatus;
  }

  getParticipants() {
    return {
      owner: this._owner,
      approver: this._approver,
    };
  }

  getAttachments() {
    return this._attachments;
  }

  // TODO: improve replace this methods by action.status === ActionStatus on use
  isOpen() {
    return this._status === ActionStatus.OPEN;
  }

  isDraft() {
    return this._status === ActionStatus.DRAFT;
  }

  isCompleted() {
    return this._status === ActionStatus.COMPLETED;
  }

  isInProgress() {
    return this._status === ActionStatus.IN_PROGRESS;
  }

  // TODO : add is Approved, is declined
  isDiscarded() {
    return this._status === ActionStatus.DISCARDED;
  }

  isPending() {
    return this._status === ActionStatus.PENDING;
  }

  isToBeApproved() {
    return this._status === ActionStatus.TO_BE_APPROVED;
  }

  isOwner(userId: string) {
    return this._owner && this._owner.id === userId;
  }

  isApprover(userId: string) {
    return this._approver && this._approver.id === userId;
  }

  isSameRecord({ id }: { id: string }) {
    return this._id === id;
  }

  canBeReassigned(actor: ActorUserContext) {
    return (
      (this.isOwner(actor.id) || this.isApprover(actor.id)) &&
      !ActionStatusNotActionable.includes(this._status)
    );
  }

  changeStatus(newStatus: ActionStatus) {
    if (!Action.canTransition(this._status, newStatus)) {
      throw new InvalidActionTransitionError(this._status, newStatus);
    }
    this._status = newStatus;
  }

  static canTransition(
    currentStatus: ActionStatus,
    newStatus: ActionStatus,
  ): boolean {
    if (currentStatus === newStatus) {
      return true;
    }
    return (
      Action.transitions[currentStatus]?.some((t) => t.to === newStatus) ??
      false
    );
  }

  /**
   * @deprecated
   * blindly sets the internal approval status of the action
   *
   * TODO: remove this method and replace with a more
   * validated/structured approach
   */
  transitionApprovalStatus(approvalStatus: ActionApprovalStatus) {
    this._approvalStatus = approvalStatus;
  }

  // TODO: this should only validate properties that are required
  open() {
    if (!this._dueDate || !this._title || !this._owner) {
      throw new ActionCanNotBeUpdatedError('Action is missing required fields');
    }

    this.changeStatus(ActionStatus.OPEN);
  }

  markAsPending() {
    this.changeStatus(ActionStatus.PENDING);
  }

  start() {
    // Only opened or pending actions can be started
    if (
      this._status !== ActionStatus.OPEN &&
      this._status !== ActionStatus.PENDING
    ) {
      throw new ActionCannotBeStartedError();
    }
    this.changeStatus(ActionStatus.IN_PROGRESS);
  }

  discard() {
    if (
      this._status !== ActionStatus.COMPLETED &&
      this._status !== ActionStatus.DRAFT
    ) {
      this.changeStatus(ActionStatus.DISCARDED);
    }
  }

  approve() {
    this._approvalStatus = ActionApprovalStatus.APPROVED;
  }

  decline({
    note,
    declinedFromIssue,
  }: {
    note: string | null;
    declinedFromIssue: boolean;
  }): void {
    const isToBeApproved =
      this._approvalStatus === ActionApprovalStatus.WAITING_FOR_APPROVAL &&
      this._status === ActionStatus.TO_BE_APPROVED;

    const isApprovedAndCompleted =
      // actions without action-approver are in not-set status
      [ActionApprovalStatus.APPROVED, ActionApprovalStatus.NOT_SET].includes(
        this._approvalStatus,
      ) && this._status === ActionStatus.COMPLETED;

    if (
      (!declinedFromIssue && !isToBeApproved) ||
      (declinedFromIssue && !isApprovedAndCompleted)
    ) {
      throw new ActionCannotBeDeclinedError();
    }
    this.changeStatus(ActionStatus.IN_PROGRESS);
    this._approvalStatus = ActionApprovalStatus.DENIED;
    this._declineNote = note;
  }

  close(authorizer: IssueAuthorizer) {
    // Only ongoing actions and actions to be approved can be finished
    if (
      this._status !== ActionStatus.IN_PROGRESS &&
      this._status !== ActionStatus.TO_BE_APPROVED
    ) {
      throw new ActionCannotBeClosed();
    }

    // Ongoing actions with approval flow need to be approved first
    if (this._approver && this._status === ActionStatus.IN_PROGRESS) {
      throw new ActionCannotBeClosed();
    }

    // Only Action Owners and Action Approvers can close actions
    if (
      !authorizer.isUserActionApprover(this) &&
      !authorizer.isUserActionOwner(this)
    ) {
      throw new UserCanNotUpdateActionError();
    }
    this.changeStatus(ActionStatus.COMPLETED);
  }

  updateValues(params: ActionUpdateValuesParams) {
    if (this.isDiscarded() || this.isCompleted()) {
      return;
    }
    const values = params;
    this.validateActionApprover(values);

    this._dueDate = values.dueDate;
    this._description = values.description;
    this._approver = values.approver;
    this._title = values.title;
    this._govmapGroups = values.govmapGroups;
    this._owner = values.owner;
    this._attachments = values.attachments || [];
    this._sequence = values.sequence;
  }

  updateAttachments(attachments: ActionQueryResponseSchemaType['attachments']) {
    this._attachments = [...(this._attachments || []), ...(attachments || [])];
  }

  removeAttachment(attachmentId: string) {
    this._attachments = this._attachments?.filter((a) => a.id !== attachmentId);
  }

  validateActionApprover(action: ActionConstructorParams) {
    if (
      this.approver &&
      !action.approver &&
      this.status &&
      [
        ActionStatus.TO_BE_APPROVED,
        ActionStatus.COMPLETED,
        ActionStatus.DISCARDED,
      ].includes(this.status)
    ) {
      throw new ActionCanNotBeUpdatedError(
        'Action approver can not be deleted',
      );
    }
  }

  addAttachments(attachments: ActionAttachmentQueryResponseType[]) {
    this._attachments = [...(this._attachments || []), ...attachments];
  }

  private generateSlug() {
    if (!this._title) {
      return null;
    }
    const slug = slugify(this._title, {
      lower: true,
      strict: true,
      remove: /[$*_+~.()'"!\-:@]/g,
    });
    // Truncate slug
    return [
      slug.slice(
        0,
        VARCHAR_MAX_LENGTH - ((this._id.length || UUIDV4_LENGTH) + 1),
      ),
      this._id,
    ]
      .filter(Boolean)
      .join('-');
  }

  clone() {
    return Object.assign(Object.create(Object.getPrototypeOf(this)), this);
  }

  toJSON(): object {
    const obj = this.toObject();
    return {
      ...obj,
      dueDate: {
        isDateInstance: obj.dueDate instanceof Date,
        isStringInstance: typeof obj.dueDate === 'string',
        value: obj.dueDate,
      },
    };
  }

  // There is not filter per property
  toObject(): VisibleActionAttributes {
    return {
      id: this.id,
      dueDate: this._dueDate ?? null,
      description: this._description ?? null,
      status: this._status,
      approver: this._approver ?? null,
      owner: this._owner ?? null,
      title: this._title,
      slugId: this._slug ?? null,
      govmapGroups: this._govmapGroups ? [...this._govmapGroups] : [],
      slaStatus: this._slaStatus ?? SLAStatusEnum.NO_PROBLEM,
      approvalStatus: this._approvalStatus ?? ActionApprovalStatus.NOT_SET,
      declineNote: this._declineNote ?? null,
      sequence: this._sequence ?? null,
      attachments: this._attachments ? [...this._attachments] : [],
      updatedAt: this._updatedAt ?? null,
      createdAt: this._createdAt ?? null,
    };
  }
}
